/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import {
  StyleSheet,
  Text,
  TextInput,
  type StyleProp,
  type TextInputProps,
  type TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { colors, spacing, typography, ticketColors } from '../theme/public-api';
import { SURFACE_CARD_RADIUS_CONTROL } from '../surface-cards/public-api';
import { posSharedUi as posUi } from './posSharedUi';
import { shouldSelectAllAfterBarcodeFieldValueChange } from '../product-search/productBarcodeInput';

const controlRadius = { borderRadius: SURFACE_CARD_RADIUS_CONTROL };

/** STOCK/UI calmado: escaneo como control secundario sin competir con CTAs ámbar. */
export type SearchScanTone = 'accent' | 'neutral';

export type ProminentSearchWithScannerProps = {
  value: string;
  onChangeText: (value: string) => void;
  /** Cámara / escáner integrado */
  onPressScan?: () => void;
  showBarcodeCameraButton?: boolean;
  placeholder: string;
  /** Si se omite, se usa `placeholder` también en layout compacto */
  placeholderCompact?: string;
  /**
   * Etiqueta sobre la fila (estilo POS).
   * - `undefined`: "Buscar o escanear"
   * - `null`: sin etiqueta (p. ej. dentro de un campo con label propio)
   */
  sectionLabel?: string | null;
  /** Ancho por debajo del cual el título pasa a variante compacta (default 400) */
  compactTitleBreakpoint?: number;
  /**
   * `pos`: siempre muestra texto "Escanear" (cabecera POS).
   * `responsive`: en anchos &lt; scanIconOnlyBreakpoint solo ícono en el botón escanear.
   */
  variant?: 'pos' | 'responsive';
  scanIconOnlyBreakpoint?: number;
  accessibilityLabel?: string;
  /** Lector HID / atajos — no duplicar en UI si `showHidHint` es false. */
  accessibilityHint?: string;
  onSubmitEditing?: () => void;
  /** @deprecated Ignorado: el buscador nunca hace autofocus al montar. */
  autoFocusOnWeb?: boolean;
  /** Mostrar hint de lector HID cuando no hay botón de cámara */
  showHidHint?: boolean;
  /** Texto del hint HID. Default: Enter / lector. */
  hidHintText?: string;
  /** Contenido después del botón escanear (ej. ícono filtros en catálogo) */
  endSlot?: React.ReactNode;
  /** Contenido al final de la sección (dentro de `searchSectionProminent`), p. ej. feedback POS */
  footer?: React.ReactNode;
  disabled?: boolean;
  showClearButton?: boolean;
  /** Ref opcional para atajos de teclado web (POS). */
  inputRef?: React.RefObject<TextInput | null>;
  /** Props extra del `TextInput` (p. ej. `autoCapitalize` en código de producto) */
  textInputProps?: Omit<TextInputProps, 'value' | 'onChangeText' | 'placeholder' | 'style' | 'editable'>;
  /** Override de estilo del input (fuente ticket, etc.). */
  inputStyle?: StyleProp<TextStyle>;
  /** `accent` (default): mismo lenguaje que POS. `neutral`: borde/texto sutiles (p. ej. pantalla Stock). */
  scanTone?: SearchScanTone;
  /**
   * Campo único bordeado (mock catálogo): input + limpiar + SCAN dentro del mismo recuadro.
   */
  unifiedField?: boolean;
  /** Override de fondo/borde del recuadro unificado (p. ej. ticket paper). */
  unifiedBoxStyle?: StyleProp<ViewStyle>;
  /** En `unifiedField`, muestra ícono junto a SCAN (el canvas usa solo texto). */
  scanShowIcon?: boolean;
  /** Muestra ícono de lupa a la izquierda del input (p. ej. POS mobile). */
  showSearchIcon?: boolean;
};

/**
 * Buscador con campo + limpiar + botón "Escanear" al estilo POS (mismos estilos `posUi`).
 * Reutilizable en POS, catálogo, stock y modales.
 *
 * HID: `selectTextOnFocus` + selección total tras submit/cámara para que el próximo
 * disparo del lector reemplace el código en vez de concatenarlo.
 */
export const ProminentSearchWithScanner = ({
  value,
  onChangeText,
  onPressScan,
  showBarcodeCameraButton = true,
  placeholder,
  placeholderCompact,
  sectionLabel,
  compactTitleBreakpoint = 400,
  variant = 'responsive',
  scanIconOnlyBreakpoint = 372,
  accessibilityLabel = 'Buscar',
  accessibilityHint,
  onSubmitEditing,
  autoFocusOnWeb: _autoFocusOnWeb = false,
  showHidHint,
  hidHintText = 'Pasá el lector por el buscador. F2 si no está enfocado.',
  endSlot,
  footer,
  disabled = false,
  showClearButton = true,
  inputRef,
  textInputProps,
  inputStyle,
  unifiedField = false,
  unifiedBoxStyle,
  scanShowIcon = true,
  showSearchIcon = false,
  scanTone = 'accent',
}: ProminentSearchWithScannerProps) => {
  const { width } = useWindowDimensions();
  const compactTitle = width < compactTitleBreakpoint;
  const scanIconOnly = variant === 'responsive' && width < scanIconOnlyBreakpoint;
  const hasSearchText = value.trim().length > 0;
  const resolvedPlaceholder = compactTitle && placeholderCompact != null ? placeholderCompact : placeholder;
  const label =
    sectionLabel === undefined ? 'Buscar o escanear' : sectionLabel === null ? null : sectionLabel;
  const showHint = showHidHint ?? (!showBarcodeCameraButton && Boolean(onSubmitEditing));
  const compactUnified = width < 400;
  const scanMuted = scanTone === 'neutral';

  const prevValueRef = useRef(value);
  const selectAllAfterSubmitRef = useRef(false);
  const [selection, setSelection] = useState<{ start: number; end: number } | undefined>(undefined);

  useEffect(() => {
    const prev = prevValueRef.current;
    prevValueRef.current = value;
    const fromSubmit = selectAllAfterSubmitRef.current;
    selectAllAfterSubmitRef.current = false;
    if (value.length === 0) {
      setSelection(undefined);
      return;
    }
    if (fromSubmit || shouldSelectAllAfterBarcodeFieldValueChange(prev, value)) {
      setSelection({ start: 0, end: value.length });
    }
  }, [value]);

  const handleSubmitEditing = () => {
    selectAllAfterSubmitRef.current = true;
    onSubmitEditing?.();
  };

  const textInputCommon = {
    ref: inputRef,
    value,
    onChangeText,
    placeholder: resolvedPlaceholder,
    placeholderTextColor: ticketColors.ink3,
    accessibilityLabel,
    accessibilityHint,
    autoCorrect: false as const,
    returnKeyType: (onSubmitEditing ? 'search' : 'default') as 'search' | 'default',
    onSubmitEditing: onSubmitEditing ? handleSubmitEditing : undefined,
    blurOnSubmit: false as const,
    ...textInputProps,
    // Tras textInputProps: HID no debe concatenar el próximo código.
    selectTextOnFocus: textInputProps?.selectTextOnFocus ?? true,
    selection,
    onSelectionChange: (e: { nativeEvent: { selection: { start: number; end: number } } }) => {
      setSelection(e.nativeEvent.selection);
      textInputProps?.onSelectionChange?.(e as never);
    },
    autoFocus: false as const,
    editable: !disabled,
  };

  if (unifiedField) {
    return (
      <View style={styles.unifiedSection}>
        {label != null ? <Text style={posUi.catalogMiniSearchLabel}>{label}</Text> : null}
        <View
          style={[
            posUi.catalogUnifiedSearchBox,
            controlRadius,
            !compactUnified && posUi.catalogUnifiedSearchBoxWide,
            unifiedBoxStyle,
          ]}
        >
          <TextInput style={[posUi.catalogUnifiedSearchInput, inputStyle]} {...textInputCommon} />
          {showClearButton && hasSearchText ? (
            <TouchableOpacity
              style={posUi.searchClearInline}
              onPress={() => onChangeText('')}
              accessibilityRole="button"
              accessibilityLabel="Limpiar búsqueda"
            >
              <Ionicons name="close-circle" size={22} color={colors.textSecondary} />
            </TouchableOpacity>
          ) : null}
          {showBarcodeCameraButton && onPressScan ? (
            <TouchableOpacity
              style={[
                posUi.catalogUnifiedScanButton,
                !compactUnified && posUi.catalogUnifiedScanButtonWide,
                scanMuted && styles.catalogUnifiedScanNeutral,
                disabled && posUi.disabledCard,
              ]}
              onPress={onPressScan}
              disabled={disabled}
              accessibilityLabel="Escanear código de barras"
              accessibilityRole="button"
            >
              {scanShowIcon ? (
                <Ionicons name="barcode-outline" size={20} color={scanMuted ? ticketColors.ink2 : ticketColors.gold} />
              ) : null}
              <Text style={[posUi.catalogUnifiedScanLabel, scanMuted && styles.catalogUnifiedScanLabelNeutral]}>SCAN</Text>
            </TouchableOpacity>
          ) : null}
          {endSlot}
        </View>
        {showHint ? <Text style={posUi.hidSearchHint}>{hidHintText}</Text> : null}
        {footer}
      </View>
    );
  }

  return (
    <View style={posUi.searchSectionProminent}>
      {label != null ? (
        <Text style={compactTitle ? posUi.searchSectionLabelCompact : posUi.searchSectionLabel}>{label}</Text>
      ) : null}
      <View style={posUi.searchRowProminent}>
        <View style={[posUi.searchInputOuter, controlRadius]}>
          {showSearchIcon ? (
            <Ionicons name="search-outline" size={16} color={ticketColors.ink3} style={{ flexShrink: 0 }} />
          ) : null}
          <TextInput style={[posUi.searchInputInnerNoBorder, inputStyle]} {...textInputCommon} />
          {showClearButton && hasSearchText ? (
            <TouchableOpacity
              style={posUi.searchClearInline}
              onPress={() => onChangeText('')}
              accessibilityRole="button"
              accessibilityLabel="Limpiar búsqueda"
            >
              <Ionicons name="close-circle" size={22} color={colors.textSecondary} />
            </TouchableOpacity>
          ) : null}
        </View>
        {showBarcodeCameraButton && onPressScan ? (
          <TouchableOpacity
            style={[
              posUi.searchScanProminent,
              scanMuted && styles.searchScanProminentNeutral,
              scanIconOnly && styles.scanIconOnly,
              disabled && posUi.disabledCard,
            ]}
            onPress={onPressScan}
            disabled={disabled}
            accessibilityLabel="Escanear código de barras"
            accessibilityRole="button"
          >
            {scanShowIcon ? (
              <Ionicons name="barcode-outline" size={24} color={scanMuted ? ticketColors.ink2 : ticketColors.gold} />
            ) : null}
            {variant === 'pos' || !scanIconOnly ? (
              <Text style={[posUi.searchScanProminentLabel, scanMuted && styles.searchScanProminentLabelNeutral]}>SCAN</Text>
            ) : null}
          </TouchableOpacity>
        ) : null}
        {endSlot}
      </View>
      {showHint ? <Text style={posUi.hidSearchHint}>{hidHintText}</Text> : null}
      {footer}
    </View>
  );
};

const styles = StyleSheet.create({
  catalogUnifiedScanNeutral: {
    borderColor: colors.controlBorder,
    backgroundColor: colors.controlSurface,
  },
  catalogUnifiedScanLabelNeutral: {
    ...typography.caption,
    color: colors.textSecondary,
    fontWeight: '700',
    fontSize: 12,
    letterSpacing: 0.28,
  },
  searchScanProminentNeutral: {
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  searchScanProminentLabelNeutral: {
    color: ticketColors.ink2,
    fontWeight: '700',
    letterSpacing: 0.25,
  },
  scanIconOnly: {
    paddingHorizontal: spacing.sm,
    minWidth: 52,
  },
  unifiedSection: {
    gap: 6,
    alignSelf: 'stretch',
    minWidth: 0,
  },
});
